// Shell branding — paints the logged-in tenant's identity onto the page.
//
// The PRODUCT mark stays Ordinex everywhere: the Quadrant O at the top of the
// rail and in the field app's lockup is inline SVG in the HTML and is never
// swapped. What the tenant gets is their own slot:
//   [data-org-name]  text elements that show the org name
//   [data-org-logo]  the workspace chip's icon — their logo when they have
//                    uploaded one, the fallback glyph when they have not
//
// The chip is the tenant's slot already (it prints their name), so putting
// their logo in it is the same statement in a better form — product mark on
// top, workspace underneath, the way every multi-workspace tool does it. This
// narrows the July 2026 rule "the tenant appears as TEXT only, the logo is for
// PDFs"; the part that still holds, and is the point of that rule, is that the
// tenant's COLOUR never touches the app. (Renan, 2026-08-14.)

import { getPdfBrand } from '../pdf/branding.js';

function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return [26, 26, 46];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export async function applyBranding(sb, org) {
  if (!org) return;

  // Synchronous brand global for the legacy PDF generators (they run in classic
  // scripts and can't await): name/address/phone/email/colors are filled
  // immediately; the logo data URL streams in when getPdfBrand resolves.
  window.OrdinexBrand = {
    name: org.name,
    address: org.branding.address || '',
    phone: org.branding.phone || '',
    email: org.branding.email || '',
    primaryRgb: hexToRgb(org.branding.primary),
    secondaryRgb: hexToRgb(org.branding.secondary || org.branding.primary),
    logoDataUrl: null,
    poweredBy: org.features?.hide_powered_by ? '' : 'Powered by Ordinex',
  };
  // getPdfBrand already fetches the logo once per session and inlines it as a
  // data URL for the PDF generators, so the chip costs no extra request.
  getPdfBrand(sb).then(b => {
    window.OrdinexBrand = b;
    paintOrgLogo(b.logoDataUrl);
  }).catch(() => {});

  // NOTE: the app UI stays the Ordinex product brand (hazard yellow --accent);
  // the tenant's brand colour is applied ONLY to generated PDFs (via
  // window.OrdinexBrand above), per docs/brand.md. Painting the tenant colour
  // onto the app's --accent was unintentional and is intentionally removed —
  // the product wears one identity for every customer. (Renan, 2026-07.)
  if (!document.title.includes(org.name)) {
    document.title = `${document.title} · ${org.name}`;
  }

  // title carries the full name where the header lockup truncates it.
  document.querySelectorAll('[data-org-name]').forEach(el => {
    el.textContent = org.name;
    el.title = org.name;
  });
}

// Swaps the workspace chip's fallback glyph for the tenant's logo.
//
// Three ways this does nothing, all of them on purpose: no slot on the page
// (the field app has none), no logo uploaded (5 of the 8 orgs in production),
// or the file fails to decode. In every one of those the glyph stays, which is
// why the markup ships WITH the glyph rather than an empty box — nothing here
// can leave a hole in the rail.
export function paintOrgLogo(dataUrl) {
  if (!dataUrl) return;
  document.querySelectorAll('[data-org-logo]').forEach(slot => {
    const painted = slot.querySelector('img');
    if (painted) { if (painted.getAttribute('src') !== dataUrl) painted.src = dataUrl; return; }   // a new logo replaces the old
    const glyph = slot.innerHTML;
    const img = new Image();
    img.src = dataUrl;
    img.alt = '';
    // A logo is somebody's mark: fit it whole inside the chip, never crop it
    // to a square and never stretch it.
    img.className = 'org-chip-logo';
    img.onload  = () => { slot.innerHTML = ''; slot.appendChild(img); slot.classList.add('has-logo'); };
    img.onerror = () => { slot.innerHTML = glyph; };     // put the glyph back
  });
}
